import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import Papa from 'papaparse';

export interface SiteReportData {
  siteName: string;
  fromDate: string;
  toDate: string;
}

export interface EventData {
  eventId: string;
  cameraId: string;
  cameraName?: string;  // Added camera name field
  eventType: string;
  eventLevel: string;
  currentLevelId?: string;  // Added for better level identification
  timestamp: string;
  siteName: string;
  eventtimestampstr?: string;
  eventtimestrinct?: string;
  actiontimestring?: string;
  [key: string]: any;
}

export interface PivotData {
  rowLabels: string[];
  columnLabels: string[];
  data: any[][];
  totals: any;
}

@Injectable({
  providedIn: 'root'
})
export class SiteReportService {

  constructor(
    private http: HttpClient
  ) { }

  downloadSiteReport(siteName: string, fromDate: string, toDate: string): Observable<string> {
    // Format the search criteria based on the actual API format
    // The API expects: "(JX Truck) Total Appearance F3605" format
    //const searchCriteria = `(${siteName}) Total Appearance F3605`;
    //const encodedSearch = encodeURIComponent(searchCriteria);
    
    // Format dates as: "2025-08-01 00:00:00" (spaces will be encoded as %20)
    const fromDateTime = `${fromDate} 00:00:00`;
    const toDateTime = `${toDate} 23:59:59`;
    // const encodedFromDate = encodeURIComponent(fromDateTime);
    // const encodedToDate = encodeURIComponent(toDateTime);
    
    const url = `https://pro-safe.pro-vigil.info:8011/api/reports/download/site?search=${siteName}&fromDate=${fromDateTime}&toDate=${toDateTime}`;
    
    console.log('Site name:', siteName);
    console.log('Search criteria:', siteName);
    console.log('API URL:', url);
    console.log('Request parameters:', { siteName, fromDate, toDate, fromDateTime, toDateTime });
    
    return this.http.get(url, { 
      responseType: 'text',
      headers: {
        'Accept': 'text/csv,text/plain,*/*',
        'Cache-Control': 'no-cache'
      }
    }).pipe(
      map(response => {
        console.log('Raw API response type:', typeof response);
        console.log('Raw API response length:', response?.length);
        
        if (!response || response === null || response === undefined) {
          throw new Error('API returned null or undefined response');
        }
        
        if (typeof response !== 'string') {
          throw new Error(`API returned unexpected data type: ${typeof response}`);
        }
        
        if (response.trim() === '') {
          throw new Error('API returned empty response');
        }
        
        return response;
      }),
      catchError(error => {
        console.error('Error downloading report:', error);
        console.error('Error details:', {
          status: error.status,
          statusText: error.statusText,
          message: error.message,
          url: url
        });
        return throwError(error);
      })
    );
  }

  async parseCSVData(csvText: string): Promise<EventData[]> {
    return new Promise((resolve, reject) => {
      // Check if csvText is valid
      if (!csvText || typeof csvText !== 'string') {
        reject(new Error('Invalid CSV data received'));
        return;
      }

      // Trim whitespace and check if empty
      const trimmedText = csvText.trim();
      if (!trimmedText) {
        reject(new Error('Empty CSV data received'));
        return;
      }

      // Check if the response might be an error message or HTML
      if (trimmedText.toLowerCase().includes('error') || 
          trimmedText.toLowerCase().includes('<!doctype') ||
          trimmedText.toLowerCase().includes('<html')) {
        reject(new Error('Received error response instead of CSV data'));
        return;
      }

      try {
        Papa.parse(trimmedText, {
          header: true,
          skipEmptyLines: true,
          dynamicTyping: false,
          transformHeader: (header) => header.trim(),
          transform: (value) => value.trim(),
          complete: (results) => {
            console.log('Papa.parse results:', results);
            if (results.errors && results.errors.length > 0) {
              console.error('CSV parsing errors:', results.errors);
              // Don't reject on parsing errors, try to use the data anyway
              console.warn('Continuing with parsed data despite errors');
            }
            
            if (results.data && Array.isArray(results.data)) {
              // Filter out empty rows and normalize data
              const validData = results.data
                .filter(row => 
                  row && typeof row === 'object' && Object.keys(row).length > 0
                )
                .map(row => {
                  // Normalize column names to match our interface
                  const normalizedRow: any = {};
                  Object.keys(row).forEach(key => {
                    const normalizedKey = key.toLowerCase().replace(/\s+/g, '');
                    normalizedRow[normalizedKey] = row[key];
                  });
                  
                  // Map to EventData interface
                  return {
                    eventId: normalizedRow.eventid || normalizedRow.id || '',
                    cameraId: normalizedRow.cameraid || normalizedRow.camera || '',
                    cameraName: normalizedRow.cameraname || normalizedRow.camera_name || normalizedRow.cameraid || normalizedRow.camera || '',
                    eventType: normalizedRow.eventtype || normalizedRow.type || '',
                    eventLevel: normalizedRow.eventlevel || normalizedRow.level || '',
                    currentLevelId: normalizedRow.currentlevelid || normalizedRow.current_level_id || normalizedRow.eventlevel || normalizedRow.level || '', // Added
                    timestamp: normalizedRow.timestamp || normalizedRow.date || normalizedRow.time || '',
                    siteName: normalizedRow.sitename || normalizedRow.site || '',
                    ...normalizedRow // Include all other fields
                  } as EventData;
                });
              
              console.log('Normalized event data:', validData);
              
              if (validData.length === 0) {
                reject(new Error('No valid data rows found in CSV'));
              } else {
                resolve(validData);
              }
            } else {
              reject(new Error('No valid data found in CSV'));
            }
          },
          error: (error) => {
            console.error('Papa.parse error:', error);
            reject(error);
          }
        });
      } catch (error) {
        console.error('Exception during CSV parsing:', error);
        reject(error);
      }
    });
  }

  createPivotTable(data: EventData[], rowField: string, columnField: string, valueField: string = 'eventId'): PivotData {
    const rowSet = new Set<string>();
    const columnSet = new Set<string>();
    const pivotMap = new Map<string, Map<string, number>>();

    // Collect unique values
    data.forEach(item => {
      console.log('item>>', item);
      let rowValue = item[rowField] || 'Unknown';
      let columnValue = item[columnField] || 'Unknown';
      
      // Handle empty event levels - replace empty strings with "No Level"
      if (columnField === 'eventLevel' && (!columnValue || columnValue.trim() === '')) {
        columnValue = 'No Level';
      }
      
      // Handle currentLevelId field
      if (columnField === 'currentLevelId' && (!columnValue || columnValue.trim() === '')) {
        columnValue = 'No Level';
      }
      
      // Handle time sorting for hour field
      if (rowField === 'hour') {
        // Keep as string for proper sorting
        rowValue = rowValue || '00:00';
      }
      
      rowSet.add(rowValue);
      columnSet.add(columnValue);
    });

    // Sort rows properly - handle time format
    let rowLabels: string[];
    if (rowField === 'hour') {
      rowLabels = Array.from(rowSet).sort((a, b) => {
        const hourA = parseInt(a.split(':')[0]);
        const hourB = parseInt(b.split(':')[0]);
        return hourA - hourB;
      });
    } else {
      rowLabels = Array.from(rowSet).sort();
    }
    
    const columnLabels = Array.from(columnSet).sort();

    // Initialize pivot map
    rowLabels.forEach(row => {
      pivotMap.set(row, new Map());
      columnLabels.forEach(col => {
        pivotMap.get(row)!.set(col, 0);
      });
    });

    // Count occurrences
    data.forEach(item => {
      let rowValue = item[rowField] || 'Unknown';
      let columnValue = item[columnField] || 'Unknown';
      
      // Handle empty event levels - replace empty strings with "No Level"
      if (columnField === 'eventLevel' && (!columnValue || columnValue.trim() === '')) {
        columnValue = 'No Level';
      }
      
      // Handle currentLevelId field
      if (columnField === 'currentLevelId' && (!columnValue || columnValue.trim() === '')) {
        columnValue = 'No Level';
      }
      
      // Handle time sorting for hour field
      if (rowField === 'hour') {
        rowValue = rowValue || '00:00';
      }
      
      const currentCount = pivotMap.get(rowValue)?.get(columnValue) || 0;
      pivotMap.get(rowValue)?.set(columnValue, currentCount + 1);
    });

    // Convert to 2D array
    const pivotData = rowLabels.map(row => {
      return columnLabels.map(col => {
        return pivotMap.get(row)?.get(col) || 0;
      });
    });

    // Calculate totals
    const rowTotals = pivotData.map(row => row.reduce((sum, val) => sum + val, 0));
    const columnTotals = columnLabels.map((_, colIndex) => 
      pivotData.reduce((sum, row) => sum + row[colIndex], 0)
    );
    const grandTotal = rowTotals.reduce((sum, val) => sum + val, 0);

    return {
      rowLabels,
      columnLabels,
      data: pivotData,
      totals: {
        rows: rowTotals,
        columns: columnTotals,
        grand: grandTotal
      }
    };
  }

  getEventTypeBreakdown(data: EventData[]): any[] {
    const eventTypeCount = new Map<string, number>();
    
    data.forEach(item => {
      const eventType = item.eventType || 'Unknown';
      eventTypeCount.set(eventType, (eventTypeCount.get(eventType) || 0) + 1);
    });

    return Array.from(eventTypeCount.entries()).map(([type, count]) => ({
      type,
      count,
      percentage: ((count / data.length) * 100).toFixed(2)
    })).sort((a, b) => b.count - a.count);
  }

  getTimeDistribution(data: EventData[]): any[] {
    const timeSlots = new Map<string, number>();
    
    // Initialize all 24 hours with 0 count
    for (let hour = 0; hour < 24; hour++) {
      const timeSlot = `${hour.toString().padStart(2, '0')}:00`;
      timeSlots.set(timeSlot, 0);
    }
    
    data.forEach(item => {
      try {
        // Use eventtimestampstr as the primary timestamp field
        let timestampStr = item.eventtimestampstr;
        
        if (!timestampStr || timestampStr.trim() === '') {
          console.warn('No valid timestamp found for item:', item.eventId);
          return;
        }
      
        const timestamp = new Date(timestampStr);
        if (isNaN(timestamp.getTime())) {
          console.warn('Invalid timestamp:', timestampStr, 'for item:', item.eventId);
          return;
        }
        
        const hour = timestamp.getHours();
        const timeSlot = `${hour.toString().padStart(2, '0')}:00`;
        timeSlots.set(timeSlot, (timeSlots.get(timeSlot) || 0) + 1);
      } catch (error) {
        console.warn('Error processing timestamp for item:', item.eventId, error);
      }
    });

    return Array.from(timeSlots.entries())
      .map(([time, count]) => ({ time, count }))
      .sort((a, b) => {
        const hourA = parseInt(a.time.split(':')[0]);
        const hourB = parseInt(b.time.split(':')[0]);
        return hourA - hourB;
      });
  }

  getCameraDistribution(data: EventData[]): any[] {
    const cameraCount = new Map<string, number>();
    
    data.forEach(item => {
      // Use camera name if available, otherwise fall back to camera ID
      const camera = item.cameraName || item.cameraId || 'Unknown';
      cameraCount.set(camera, (cameraCount.get(camera) || 0) + 1);
    });

    return Array.from(cameraCount.entries())
      .map(([camera, count]) => ({ camera, count }))
      .sort((a, b) => b.count - a.count);
  }

  getLevelDistribution(data: EventData[]): any[] {
    const levelCount = new Map<string, number>();
    
    data.forEach(item => {
      let level = item.currentLevelId || item.eventLevel || 'No Level';
      if (!level || level.trim() === '') {
        level = 'No Level';
      }
      levelCount.set(level, (levelCount.get(level) || 0) + 1);
    });

    return Array.from(levelCount.entries())
      .map(([level, count]) => ({ level, count }))
      .sort((a, b) => b.count - a.count);
  }

  getActionDistribution(data: EventData[]): any[] {
    const actionCount = new Map<string, number>();
    
    data.forEach(item => {
      // Look for action-related fields in the data
      let action = item.action || item.actionType || item.eventType || 'Unknown Action';
      if (!action || action.trim() === '') {
        action = 'Unknown Action';
      }
      actionCount.set(action, (actionCount.get(action) || 0) + 1);
    });

    return Array.from(actionCount.entries())
      .map(([action, count]) => ({ action, count }))
      .sort((a, b) => b.count - a.count);
  }

  getLevelActionLineData(data: EventData[]): any {
    const timeLevelActionMap = new Map<string, Map<string, Map<string, number>>>();
    
    // Initialize all 24 hours
    for (let hour = 0; hour < 24; hour++) {
      const timeSlot = `${hour.toString().padStart(2, '0')}:00`;
      timeLevelActionMap.set(timeSlot, new Map());
    }

    // Count events by time, level, and action
    data.forEach(item => {
      try {
        let timestampStr = item.timestamp || item.eventtimestampstr || item.eventtimestrinct || item.actiontimestring;
        if (!timestampStr || timestampStr.trim() === '') {
          return;
        }
        
        const timestamp = new Date(timestampStr);
        if (isNaN(timestamp.getTime())) {
          return;
        }
        
        const hour = timestamp.getHours();
        const timeSlot = `${hour.toString().padStart(2, '0')}:00`;
        const level = item.currentLevelId || item.eventLevel || 'No Level';
        const action = item.action || item.actionType || item.eventType || 'Unknown Action';
        
        const levelMap = timeLevelActionMap.get(timeSlot);
        if (levelMap) {
          if (!levelMap.has(level)) {
            levelMap.set(level, new Map());
          }
          const actionMap = levelMap.get(level);
          if (actionMap) {
            actionMap.set(action, (actionMap.get(action) || 0) + 1);
          }
        }
      } catch (error) {
        console.warn('Error processing timestamp for item:', item.eventId, error);
      }
    });

    // Get all unique levels and actions
    const allLevels = new Set<string>();
    const allActions = new Set<string>();
    
    timeLevelActionMap.forEach(levelMap => {
      levelMap.forEach(actionMap => {
        levelMap.forEach((_, level) => allLevels.add(level));
        actionMap.forEach((_, action) => allActions.add(action));
      });
    });

    const levelArray = Array.from(allLevels).sort();
    const actionArray = Array.from(allActions).sort();
    const timeArray = Array.from(timeLevelActionMap.keys()).sort((a, b) => {
      const hourA = parseInt(a.split(':')[0]);
      const hourB = parseInt(b.split(':')[0]);
      return hourA - hourB;
    });

    // Create datasets for each level-action combination
    const datasets: any[] = [];
    levelArray.forEach(level => {
      actionArray.forEach(action => {
        const data = timeArray.map(timeSlot => {
          const levelMap = timeLevelActionMap.get(timeSlot);
          if (levelMap && levelMap.has(level)) {
            const actionMap = levelMap.get(level);
            return actionMap ? (actionMap.get(action) || 0) : 0;
          }
          return 0;
        });
        
        // Only include datasets that have non-zero data
        const hasData = data.some(value => value > 0);
        if (hasData) {
          datasets.push({
            label: `${level} - ${action}`,
            data: data,
            borderColor: this.generateLineChartColor(datasets.length),
            backgroundColor: this.generateLineChartColor(datasets.length, 0.1),
            borderWidth: 2,
            fill: false,
            tension: 0.1
          });
        }
      });
    });

    return {
      labels: timeArray,
      datasets: datasets
    };
  }

  getConsolidatedLevelActionsData(data: EventData[]): any {
    const timeLevelMap = new Map<string, Map<string, number>>();
    
    // Initialize all 24 hours
    for (let hour = 0; hour < 24; hour++) {
      const timeSlot = `${hour.toString().padStart(2, '0')}:00`;
      timeLevelMap.set(timeSlot, new Map());
    }

    // Count events by time and level (consolidating all actions within each level)
    data.forEach(item => {
      try {
        let timestampStr = item.timestamp || item.eventtimestampstr || item.eventtimestrinct || item.actiontimestring;
        if (!timestampStr || timestampStr.trim() === '') {
          return;
        }
        
        const timestamp = new Date(timestampStr);
        if (isNaN(timestamp.getTime())) {
          return;
        }
        
        const hour = timestamp.getHours();
        const timeSlot = `${hour.toString().padStart(2, '0')}:00`;
        const level = item.currentLevelId || item.eventLevel || 'No Level';
        
        const levelMap = timeLevelMap.get(timeSlot);
        if (levelMap) {
          levelMap.set(level, (levelMap.get(level) || 0) + 1);
        }
      } catch (error) {
        console.warn('Error processing timestamp for item:', item.eventId, error);
      }
    });

    // Get all unique levels
    const allLevels = new Set<string>();
    timeLevelMap.forEach(levelMap => {
      levelMap.forEach((_, level) => {
        allLevels.add(level);
      });
    });

    const levelArray = Array.from(allLevels).sort();
    const timeArray = Array.from(timeLevelMap.keys()).sort((a, b) => {
      const hourA = parseInt(a.split(':')[0]);
      const hourB = parseInt(b.split(':')[0]);
      return hourA - hourB;
    });

    // Create datasets for each level (consolidated actions)
    const datasets: any[] = [];
    levelArray.forEach(level => {
      const data = timeArray.map(timeSlot => {
        const levelMap = timeLevelMap.get(timeSlot);
        return levelMap ? (levelMap.get(level) || 0) : 0;
      });
      
      // Only include datasets that have non-zero data
      const hasData = data.some(value => value > 0);
      if (hasData) {
        datasets.push({
          label: `Level: ${level}`,
          data: data,
          borderColor: this.generateLineChartColor(datasets.length),
          backgroundColor: this.generateLineChartColor(datasets.length, 0.1),
          borderWidth: 3,
          fill: false,
          tension: 0.2,
          pointRadius: 4,
          pointHoverRadius: 6
        });
      }
    });

    return {
      labels: timeArray,
      datasets: datasets
    };
  }

  private generateLineChartColor(index: number, alpha: number = 1): string {
    const colors = [
      `rgba(255, 99, 132, ${alpha})`,   // Red
      `rgba(54, 162, 235, ${alpha})`,   // Blue
      `rgba(255, 205, 86, ${alpha})`,   // Yellow
      `rgba(75, 192, 192, ${alpha})`,   // Teal
      `rgba(153, 102, 255, ${alpha})`,  // Purple
      `rgba(255, 159, 64, ${alpha})`,   // Orange
      `rgba(199, 199, 199, ${alpha})`,  // Grey
      `rgba(83, 102, 255, ${alpha})`,   // Indigo
      `rgba(255, 99, 255, ${alpha})`,   // Pink
      `rgba(99, 255, 132, ${alpha})`    // Green
    ];
    return colors[index % colors.length];
  }

  // Test method to debug API response
  testApiResponse(siteName: string, fromDate: string, toDate: string): Observable<any> {
    const searchCriteria = `(${siteName}) Total Appearance F3605`;
    const encodedSearch = encodeURIComponent(searchCriteria);
    const fromDateTime = `${fromDate} 00:00:00`;
    const toDateTime = `${toDate} 00:00:00`;
    const encodedFromDate = encodeURIComponent(fromDateTime);
    const encodedToDate = encodeURIComponent(toDateTime);
    
    const url = `https://pro-safe.pro-vigil.info:8011/api/reports/download/site?search=${encodedSearch}&fromDate=${encodedFromDate}&toDate=${encodedToDate}`;
    
    console.log('Testing API URL:', url);
    console.log('Site name:', siteName);
    console.log('Search criteria:', searchCriteria);
    
    return this.http.get(url, { 
      responseType: 'text',
      observe: 'response',
      headers: {
        'Accept': 'text/csv,text/plain,*/*'
      }
    }).pipe(
      catchError(error => {
        console.error('API test error:', error);
        return throwError(error);
      })
    );
  }
}
